import React from 'react';
import { type SieveFilterModel } from '~/util/sieve/filter';
import { toSieveSortType, type SieveSortDirection } from '~/util/sieve/sort';
import { type SearchOptionType, type SearchTypeToModel } from '@generated';
import { useSearch } from '~/util/http/useSearch';
import { useQueryDataTableOptions } from '~/shared/data-tables/query/useQueryDataTableOptions';
import { keepPreviousData } from '@tanstack/react-query';
import type { SortDescriptor } from 'react-aria-components';
import type { DataTableColumnProps } from '../DataTableColumn';
import type { DataTablePagerConfig } from '../DataTablePager';

export type QueryDataTableType = 'url' | 'inner-with-filters' | 'inner';

export type QueryDataTableColumn<T extends SearchOptionType> = DataTableColumnProps<SearchTypeToModel[T]> & {
	filterNames?: string[];
};

export interface UseServerDataTableOptions<T extends SearchOptionType> {
	columns?: QueryDataTableColumn<T>[];
	type?: QueryDataTableType;
	staticFilters?: SieveFilterModel[];
	enabled?: boolean;
	pageSize?: number;
	refetchInterval?: number | false;
}

export interface ServerDataTable<T extends SearchOptionType> {
	rows: SearchTypeToModel[T][];
	isLoading: boolean;
	pager: DataTablePagerConfig;
	sortDescriptor: SortDescriptor | undefined;
	onSortChange: (descriptor: SortDescriptor) => void;
	exportState: ServerDataTableExportState;
}

export interface ServerDataTableExportState {
	filters: SieveFilterModel[];
	sortColumn: string;
	sortDirection: string;
	page: number;
	pageSize: number;
}

interface InnerOptions {
	page: number;
	pageSize: number;
	sortColumn: string;
	sortDirection: SieveSortDirection;
}

function replaceFilterNamesWithField<T extends SearchOptionType>(
	columns: QueryDataTableColumn<T>[],
	filters: SieveFilterModel[]
): SieveFilterModel[] {
	return filters.map(filter => {
		const column = columns.find(col => col.filterNames?.includes(filter.name));
		return column ? { ...filter, name: column.key } : filter;
	});
}

export function useServerDataTable<T extends SearchOptionType>(searchType: T, options: UseServerDataTableOptions<T> = {}): ServerDataTable<T> {
	const { columns = [], type = 'url', staticFilters, enabled, pageSize: pageSizeProp, refetchInterval } = options;

	const { searchOptions, setSearchOptions } = useQueryDataTableOptions();
	const [inner, setInner] = React.useState<InnerOptions>({
		page: 1,
		pageSize: pageSizeProp ?? 10,
		sortColumn: '',
		sortDirection: 'asc'
	});

	const isUrl = type === 'url';

	const page = isUrl ? searchOptions.page : inner.page;
	const pageSize = isUrl ? searchOptions.pageSize : inner.pageSize;

	const filters =
		type !== 'inner' ? [...replaceFilterNamesWithField(columns, searchOptions.filters), ...(staticFilters ?? [])] : staticFilters ?? [];

	const { data, isLoading, count, sortColumn, sortDirection, isFetching } = useSearch({
		searchType,
		page,
		pageSize,
		filters,
		sortColumn: isUrl ? searchOptions.sortColumn : inner.sortColumn,
		sortDirection: isUrl ? toSieveSortType(searchOptions.sortDirection) : inner.sortDirection,
		enabled,
		refetchInterval,
		placeholderData: keepPreviousData
	});

	const setPage = (nextPage: number) => {
		if (nextPage === page) return;
		if (isUrl) setSearchOptions(prev => ({ ...prev, page: nextPage }));
		else setInner(prev => ({ ...prev, page: nextPage }));
	};

	const setPageSize = (nextPageSize: number) => {
		if (nextPageSize === pageSize) return;
		if (isUrl) setSearchOptions(prev => ({ ...prev, pageSize: nextPageSize, page: 1 }));
		else setInner(prev => ({ ...prev, pageSize: nextPageSize, page: 1 }));
	};

	const onSortChange = (descriptor: SortDescriptor) => {
		const nextColumn = descriptor.column ? String(descriptor.column) : '';
		const nextDirection: SieveSortDirection = descriptor.direction === 'descending' ? 'desc' : 'asc';
		const currentColumn = isUrl ? searchOptions.sortColumn : inner.sortColumn;
		const currentDirection = isUrl ? searchOptions.sortDirection : inner.sortDirection;
		if (nextColumn === currentColumn && nextDirection === currentDirection) return;
		if (isUrl) setSearchOptions(prev => ({ ...prev, sortColumn: nextColumn, sortDirection: nextDirection }));
		else setInner(prev => ({ ...prev, sortColumn: nextColumn, sortDirection: nextDirection }));
	};

	const sortDescriptor: SortDescriptor | undefined = sortColumn
		? { column: sortColumn, direction: sortDirection === 'desc' ? 'descending' : 'ascending' }
		: undefined;

	const pager: DataTablePagerConfig = {
		mode: 'server',
		pageSize,
		currentPage: page,
		totalCount: count,
		onPageChange: setPage,
		onPageSizeChange: setPageSize
	};

	return {
		rows: data ?? [],
		isLoading: isLoading || isFetching,
		pager,
		sortDescriptor,
		onSortChange,
		exportState: {
			filters,
			sortColumn: sortColumn ?? '',
			sortDirection: sortDirection ?? 'asc',
			page,
			pageSize
		}
	};
}
